import './src/styles/tailwind.css';

import React from 'react';

import type { GatsbyBrowser } from 'gatsby';

import { PageWrapper } from './src/components/PageWrapper';
import { getPageLocaleFromPath } from './src/utils/i18nUtils';
import Cookies from 'universal-cookie';
import { allowCookies } from './src/components/CookieConsent';

type ScrollToOption = {
  top: number;
  left: number;
  behavior: 'auto' | 'smooth' | 'instant';
};

const hasPosition = (maybePosition: unknown): maybePosition is [number, number] =>
  !!(
    maybePosition &&
    typeof maybePosition?.[0] === 'number' &&
    typeof maybePosition?.[1] === 'number'
  );

const scrollToAnchor = (location) => {
  // Check for location so build does not fail
  if (location && location.hash) {
    const id = decodeURIComponent(location.hash.replace('#', ''));
    const item = document.querySelector(`[id='${id}']`);
    if (item) {
      item.scrollIntoView();
    }
  }
  return true;
};

const activateCookies = () => {
  const cookies = new Cookies();
  if (cookies.get('aatos-cookie-consent')) {
    allowCookies();
  }
};

export const onRouteUpdate = ({ location }) => {
  window.dataLayer = window.dataLayer || [];
  activateCookies();
  scrollToAnchor(location);
};

export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = (args) => {
  const {
    routerProps: { location },
    prevRouterProps,
    getSavedScrollPosition,
  } = args;

  if (prevRouterProps?.location && prevRouterProps.location.pathname === location.pathname) {
    return true;
  } else {
    const currentPosition: unknown = getSavedScrollPosition(location);

    const useOldPosition = hasPosition(currentPosition);

    const position: ScrollToOption = useOldPosition
      ? { top: currentPosition[1], left: currentPosition[0], behavior: 'instant' }
      : { top: 0, left: 0, behavior: 'instant' };

    window.scrollTo(position as any); // instant is not in the type definition even though it's supported by most browsers - auto doesn't work in this case

    return false;
  }
};

export const wrapPageElement = ({ element, props }: { element: React.ReactNode; props: any }) => {
  const locale = getPageLocaleFromPath(props.location.pathname);
  return <PageWrapper locale={locale}>{element}</PageWrapper>;
};
